import React, { useState } from 'react';
import {
  ClockIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  BellIcon,
  CogIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlayCircleIcon,
  PlusIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

// DDD Imports
import { useOperationalRulesData } from '../use-cases';
import { operationalRulesService } from '../services';
import type { AutoCheckoutRule, ReservationRule, SeatMovementRule, NotificationRule, OperatingHoursRule, SelfStudyRule, SelfStudyWindowPreview, MembershipRule, ScheduleApprovalRule } from '../models';
import SelfStudyHolidays from './SelfStudyHolidays';
import { API_BASE_URL } from '../../../config/api';

const OperationalRules: React.FC = () => {
  // DDD Hook
  const {
    rules,
    loading,
    error,
    activeTab,
    simulationResult,
    showSimulation,
    setActiveTab,
    setShowSimulation,
    updateAutoCheckoutRule,
    updateReservationRule,
    updateSeatMovementRule,
    updateNotificationRule,
    updateOperatingHoursRule,
    updateSelfStudyRule,
    updateMembershipRule,
    updateScheduleApprovalRule,
    runAutoCheckoutSimulation,
    runReservationSimulation,
    resetToDefaults,
  } = useOperationalRulesData();

  // Local UI State (forms)
  const [autoCheckoutForm, setAutoCheckoutForm] = useState<AutoCheckoutRule>({
    enabled: true,
    grace_period_minutes: 30,
    warning_minutes: [10, 5],
    force_checkout_after_minutes: 60,
  });

  const [reservationForm, setReservationForm] = useState<ReservationRule>({
    enabled: true,
    max_advance_hours: 24,
    min_advance_minutes: 30,
    no_show_timeout_minutes: 15,
    free_cancellation_hours: 2,
    max_daily_reservations: 3,
  });

  const [movementForm, setMovementForm] = useState<SeatMovementRule>({
    enabled: true,
    same_zone_fee: 0,
    different_zone_fee: 1000,
    premium_upgrade_policy: 'pay_difference',
    max_movements_per_session: 3,
  });

  const [notificationForm, setNotificationForm] = useState<NotificationRule>({
    enabled: true,
    checkout_warnings: [30, 10, 5],
    reservation_reminders: [60, 30],
    send_sms: false,
    send_push: true,
    send_email: false,
  });

  const [operatingHoursForm, setOperatingHoursForm] = useState<OperatingHoursRule>({
    enabled: true,
    weekday_open: '09:00',
    weekday_close: '22:00',
    weekend_open: '10:00',
    weekend_close: '18:00',
    day_boundary_hour: 3,
    description: '날짜 구분 기준 시간 (예: 26일 데이터 조회 시 27일 03:00까지 포함)',
  });

  // 자율학습 구간 (2026-07-28)
  const [selfStudyForm, setSelfStudyForm] = useState<SelfStudyRule>({
    enabled: true,
    weekday_start: '22:00',
    saturday_start: '19:10',
    sunday_all_day: true,
    holiday_all_day: true,
    end_time: '02:00',
    grace_minutes: 0,
    penalty_enabled: false,
    notify: false,
  });
  const [selfStudyPreview, setSelfStudyPreview] = useState<SelfStudyWindowPreview | null>(null);

  // 이용권(결제) 관리 사용 여부 (2026-08-18)
  const [membershipForm, setMembershipForm] = useState<MembershipRule>({
    voucher_management_enabled: true,
  });

  // 스케줄 변경 운영자 승인 필요 여부 (2026-08-19)
  const [scheduleApprovalForm, setScheduleApprovalForm] = useState<ScheduleApprovalRule>({
    owner_approval_required: true,
  });

  // Sync forms with loaded rules
  React.useEffect(() => {
    if (rules) {
      setAutoCheckoutForm(rules.auto_checkout);
      setReservationForm(rules.reservation);
      setMovementForm(rules.seat_movement);
      setNotificationForm(rules.notifications);
      if (rules.operating_hours) {
        setOperatingHoursForm(rules.operating_hours);
      }
      if (rules.self_study) {
        // 저장된 값에 없는 항목(나중에 추가된 설정)은 기본값이 남도록 덮어쓰기 방식으로 병합.
        // 통째로 교체하면 그 항목이 undefined 가 되어 체크박스가 동작하지 않는다.
        setSelfStudyForm((prev) => ({ ...prev, ...rules.self_study }));
      }
      if (rules.membership) {
        setMembershipForm((prev) => ({ ...prev, ...rules.membership }));
      }
      if (rules.schedule_approval) {
        setScheduleApprovalForm((prev) => ({ ...prev, ...rules.schedule_approval }));
      }
    }
  }, [rules]);

  // 오늘의 자율학습 구간 미리보기 — 설정이 실제로 어떻게 적용되는지 보여준다
  React.useEffect(() => {
    if (activeTab !== 'self-study') return;
    const token = localStorage.getItem('accessToken') || 'demo-token';
    fetch(`${API_BASE_URL}/api/v1/owner/holidays/self-study-window`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setSelfStudyPreview(data))
      .catch(() => setSelfStudyPreview(null));
  }, [activeTab, rules]);

  // Event Handlers
  const handleSaveAutoCheckout = async () => {
    await updateAutoCheckoutRule(autoCheckoutForm);
  };

  const handleSaveReservation = async () => {
    await updateReservationRule(reservationForm);
  };

  const handleSaveMovement = async () => {
    await updateSeatMovementRule(movementForm);
  };

  const handleSaveNotification = async () => {
    await updateNotificationRule(notificationForm);
  };

  const handleSaveOperatingHours = async () => {
    await updateOperatingHoursRule(operatingHoursForm);
  };

  const handleSaveSelfStudy = async () => {
    await updateSelfStudyRule(selfStudyForm);
  };

  const handleSaveMembership = async () => {
    await updateMembershipRule(membershipForm);
  };

  const handleSaveScheduleApproval = async () => {
    await updateScheduleApprovalRule(scheduleApprovalForm);
  };

  // Render Functions
  const renderAutoCheckoutTab = () => (
    <div className="space-y-6">
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
        ⚠️ 이 기능은 현재 미구현입니다. 설정값은 저장되나 실제 자동 퇴실 동작은 적용되지 않습니다. (향후 구현 예정)
      </div>
      <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
        <ClockIcon className="h-6 w-6 text-blue-600" />
        <div>
          <h3 className="font-medium text-blue-900">자동 퇴실 규칙</h3>
          <p className="text-sm text-blue-700">이용 시간 종료 후 자동으로 퇴실 처리하는 규칙을 설정합니다.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">활성화</label>
          <input
            type="checkbox"
            checked={autoCheckoutForm.enabled}
            onChange={(e) => setAutoCheckoutForm({ ...autoCheckoutForm, enabled: e.target.checked })}
            className="w-4 h-4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">유예 시간 (분)</label>
          <input
            type="number"
            value={autoCheckoutForm.grace_period_minutes}
            onChange={(e) =>
              setAutoCheckoutForm({ ...autoCheckoutForm, grace_period_minutes: parseInt(e.target.value) })
            }
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">강제 퇴실 시간 (분)</label>
          <input
            type="number"
            value={autoCheckoutForm.force_checkout_after_minutes}
            onChange={(e) =>
              setAutoCheckoutForm({ ...autoCheckoutForm, force_checkout_after_minutes: parseInt(e.target.value) })
            }
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={handleSaveAutoCheckout} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          저장
        </button>
        <button onClick={runAutoCheckoutSimulation} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          시뮬레이션 실행
        </button>
      </div>
    </div>
  );

  const renderReservationTab = () => (
    <div className="space-y-6">
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
        ⚠️ 예약 기능은 현재 미구현입니다. 설정값은 저장되나 실제 예약 시스템은 동작하지 않습니다.
      </div>
      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
        <CalendarDaysIcon className="h-6 w-6 text-green-600" />
        <div>
          <h3 className="font-medium text-green-900">예약 규칙</h3>
          <p className="text-sm text-green-700">좌석 예약 및 노쇼 관리 규칙을 설정합니다.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">활성화</label>
          <input
            type="checkbox"
            checked={reservationForm.enabled}
            onChange={(e) => setReservationForm({ ...reservationForm, enabled: e.target.checked })}
            className="w-4 h-4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">최대 사전 예약 시간 (시간)</label>
          <input
            type="number"
            value={reservationForm.max_advance_hours}
            onChange={(e) => setReservationForm({ ...reservationForm, max_advance_hours: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">일일 최대 예약 횟수</label>
          <input
            type="number"
            value={reservationForm.max_daily_reservations}
            onChange={(e) => setReservationForm({ ...reservationForm, max_daily_reservations: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">무료 취소 가능 시간 (시간)</label>
          <input
            type="number"
            value={reservationForm.free_cancellation_hours}
            onChange={(e) => setReservationForm({ ...reservationForm, free_cancellation_hours: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={handleSaveReservation} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          저장
        </button>
        <button onClick={runReservationSimulation} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          시뮬레이션 실행
        </button>
      </div>
    </div>
  );

  const renderMovementTab = () => (
    <div className="space-y-6">
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
        ⚠️ 좌석 이동 요금 기능은 현재 미구현입니다. 설정값은 저장되나 실제 수수료 부과는 적용되지 않습니다.
      </div>
      <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
        <ArrowPathIcon className="h-6 w-6 text-purple-600" />
        <div>
          <h3 className="font-medium text-purple-900">좌석 이동 규칙</h3>
          <p className="text-sm text-purple-700">좌석 이동 수수료 및 제한 규칙을 설정합니다.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">활성화</label>
          <input
            type="checkbox"
            checked={movementForm.enabled}
            onChange={(e) => setMovementForm({ ...movementForm, enabled: e.target.checked })}
            className="w-4 h-4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">동일 구역 이동 수수료 (원)</label>
          <input
            type="number"
            value={movementForm.same_zone_fee}
            onChange={(e) => setMovementForm({ ...movementForm, same_zone_fee: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">다른 구역 이동 수수료 (원)</label>
          <input
            type="number"
            value={movementForm.different_zone_fee}
            onChange={(e) => setMovementForm({ ...movementForm, different_zone_fee: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">세션당 최대 이동 횟수</label>
          <input
            type="number"
            value={movementForm.max_movements_per_session}
            onChange={(e) => setMovementForm({ ...movementForm, max_movements_per_session: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <button onClick={handleSaveMovement} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
        저장
      </button>
    </div>
  );

  const renderNotificationTab = () => (
    <div className="space-y-6">
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
        ⚠️ SMS/푸시/이메일 발송 기능은 현재 미구현입니다. <strong>카카오 알림톡 사용 여부는 사이드바 → 설정 → 연동 설정 메뉴에서 관리하세요.</strong> 이 탭의 토글은 향후 채널 구현 시점에 적용됩니다.
      </div>
      <div className="flex items-center gap-4 p-4 bg-yellow-50 rounded-lg">
        <BellIcon className="h-6 w-6 text-yellow-600" />
        <div>
          <h3 className="font-medium text-yellow-900">알림 규칙</h3>
          <p className="text-sm text-yellow-700">사용자에게 발송할 알림 규칙을 설정합니다.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">활성화</label>
          <input
            type="checkbox"
            checked={notificationForm.enabled}
            onChange={(e) => setNotificationForm({ ...notificationForm, enabled: e.target.checked })}
            className="w-4 h-4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">SMS 알림</label>
          <input
            type="checkbox"
            checked={notificationForm.send_sms}
            onChange={(e) => setNotificationForm({ ...notificationForm, send_sms: e.target.checked })}
            className="w-4 h-4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">푸시 알림</label>
          <input
            type="checkbox"
            checked={notificationForm.send_push}
            onChange={(e) => setNotificationForm({ ...notificationForm, send_push: e.target.checked })}
            className="w-4 h-4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">이메일 알림</label>
          <input
            type="checkbox"
            checked={notificationForm.send_email}
            onChange={(e) => setNotificationForm({ ...notificationForm, send_email: e.target.checked })}
            className="w-4 h-4"
          />
        </div>
      </div>

      <button onClick={handleSaveNotification} className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
        저장
      </button>
    </div>
  );

  const renderOperatingHoursTab = () => (
    <div className="space-y-6">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
        ℹ️ <strong>날짜 구분 기준 시간(day_boundary_hour)</strong> 은 실제로 시스템 동작에 적용됩니다 (순공부시간 일자 경계). 평일/주말 오픈·마감 시간은 현재 정보 저장만 됩니다.
      </div>
      <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-lg">
        <ClockIcon className="h-6 w-6 text-indigo-600" />
        <div>
          <h3 className="font-medium text-indigo-900">운영시간 설정</h3>
          <p className="text-sm text-indigo-700">독서실 운영시간 및 순공부시간 계산 기준을 설정합니다.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 평일 운영시간 */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">평일 운영시간</h4>
          <div>
            <label className="block text-sm font-medium mb-2">오픈 시간</label>
            <input
              type="time"
              value={operatingHoursForm.weekday_open}
              onChange={(e) => setOperatingHoursForm({ ...operatingHoursForm, weekday_open: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">마감 시간</label>
            <input
              type="time"
              value={operatingHoursForm.weekday_close}
              onChange={(e) => setOperatingHoursForm({ ...operatingHoursForm, weekday_close: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* 주말 운영시간 */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">주말 운영시간</h4>
          <div>
            <label className="block text-sm font-medium mb-2">오픈 시간</label>
            <input
              type="time"
              value={operatingHoursForm.weekend_open}
              onChange={(e) => setOperatingHoursForm({ ...operatingHoursForm, weekend_open: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">마감 시간</label>
            <input
              type="time"
              value={operatingHoursForm.weekend_close}
              onChange={(e) => setOperatingHoursForm({ ...operatingHoursForm, weekend_close: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* 날짜 구분 기준 시간 */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900">순공부시간 계산 기준</h4>
        <div>
          <label className="block text-sm font-medium mb-2">
            날짜 구분 기준 시간 (새벽 몇 시까지를 전날로 계산할지)
          </label>
          <select
            value={operatingHoursForm.day_boundary_hour}
            onChange={(e) => setOperatingHoursForm({ ...operatingHoursForm, day_boundary_hour: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                새벽 {i}시
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-gray-600">
            {operatingHoursForm.description}
          </p>
          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>예시:</strong> 기준 시간을 "새벽 3시"로 설정하면,<br />
              26일 공부 시간 조회 시 → 26일 00:00부터 27일 03:00까지 포함됩니다.
            </p>
          </div>
        </div>
      </div>

      <button onClick={handleSaveOperatingHours} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
        저장
      </button>
    </div>
  );

  // ⭐ 자율학습 구간 (2026-07-28)
  const renderSelfStudyTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-rose-50 rounded-lg">
        <ClockIcon className="h-6 w-6 text-rose-600" />
        <div>
          <h3 className="font-medium text-rose-900">자율학습 시간 설정</h3>
          <p className="text-sm text-rose-700">
            자율학습 시간대는 <strong>입·퇴실이 자유</strong>롭고 교시별 출결을 따지지 않습니다.
            다만 스케줄을 등록해 두고 <strong>안 오거나 늦으면 벌점</strong>이 부과됩니다.
          </p>
        </div>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800 space-y-1">
        <p>ℹ️ <strong>교시 시간표와 종소리는 바뀌지 않습니다.</strong> 지금 설정 그대로 유지됩니다.</p>
        <p>ℹ️ 이 설정은 <strong>시간 구간을 나누는 것</strong>입니다. 자율 시간대에 벌점을 매길지는 아래 <strong>「자율학습 시간에도 벌점 매기기」</strong>로 따로 켭니다.</p>
        <p>ℹ️ 벌점을 켜면 자율 종료 시각(새벽 2시)에 판정되어 <strong>벌점 검수 목록</strong>에 올라갑니다. 바로 부과되지 않고 원장님이 확인 후 부여합니다.</p>
        <p>ℹ️ 자율학습 구간에서 생긴 벌점은 <strong>학생·학부모에게 알림을 보내지 않습니다.</strong> (아래에서 켤 수 있습니다)</p>
      </div>

      {/* 오늘 적용 상태 미리보기 */}
      {selfStudyPreview && (
        <div className="p-4 border rounded-lg bg-white">
          <h4 className="font-medium text-gray-900 mb-2">오늘 적용 상태</h4>
          <p className="text-sm text-gray-700">
            <strong>{selfStudyPreview.date} ({selfStudyPreview.weekday})</strong>
            {selfStudyPreview.is_holiday && <span className="ml-2 px-2 py-0.5 text-xs bg-rose-100 text-rose-700 rounded">공휴일</span>}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            {selfStudyPreview.self_study_start ? (
              selfStudyPreview.all_day_self_study ? (
                <>하루 전체 자율학습 — <strong>{selfStudyPreview.self_study_start} ~ {selfStudyPreview.self_study_end}</strong> (정규 구간 없음)</>
              ) : (
                <>정규 구간 마감 <strong>{selfStudyPreview.regular_deadline}</strong> → 자율학습 <strong>{selfStudyPreview.self_study_start} ~ {selfStudyPreview.self_study_end}</strong></>
              )
            ) : (
              <>자율학습 구간이 설정되어 있지 않습니다.</>
            )}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <p className="font-medium">자율학습 구간 사용</p>
          <p className="text-sm text-gray-600">끄면 모든 시간대가 기존처럼 교시별로만 판정됩니다.</p>
        </div>
        {/* ⚠️ index.css 의 .h-6/.w-6 가 폭·높이를 16px로 강제해서(정사각형 버그)
            peer 토글 마크업이 찌그러진다 → 이 페이지 다른 탭과 같은 기본 체크박스 사용 */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selfStudyForm.enabled}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, enabled: e.target.checked })}
            style={{ width: '1.15rem', height: '1.15rem' }}
            className="rounded accent-rose-600"
          />
          <span className="text-sm text-gray-700">{selfStudyForm.enabled ? '사용 중' : '사용 안 함'}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">평일 자율 시작 (월~금)</label>
          <input
            type="time"
            value={selfStudyForm.weekday_start}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, weekday_start: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500"
          />
          <p className="mt-1 text-xs text-gray-500">이 시각부터 자율학습으로 처리됩니다.</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">토요일 자율 시작</label>
          <input
            type="time"
            value={selfStudyForm.saturday_start}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, saturday_start: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500"
          />
          <p className="mt-1 text-xs text-gray-500">기본값 19:10 (6교시 종료 후 7교시 시작 시각).</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">자율 종료 시각 (다음 날 새벽)</label>
          <input
            type="time"
            value={selfStudyForm.end_time}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, end_time: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500"
          />
          <p className="mt-1 text-xs text-gray-500">이 시각까지를 자율학습 시간으로 봅니다. (기본 새벽 2시)</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">지각 유예 시간 (분)</label>
          <input
            type="number"
            min={0}
            max={120}
            value={selfStudyForm.grace_minutes}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, grace_minutes: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500"
          />
          <p className="mt-1 text-xs text-gray-500">이 시간을 넘겨 도착하면 지각으로 봅니다.</p>
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={selfStudyForm.sunday_all_day}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, sunday_all_day: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm">
            <strong>일요일은 하루 전체 자율학습</strong>
            <span className="block text-gray-500 text-xs">개원(첫 교시) 시각부터 자율로 처리합니다.</span>
          </span>
        </label>

        <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={selfStudyForm.holiday_all_day}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, holiday_all_day: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm">
            <strong>공휴일(빨간날)도 하루 전체 자율학습</strong>
            <span className="block text-gray-500 text-xs">아래 휴일 목록에 있는 날에 적용됩니다.</span>
          </span>
        </label>

        <label className="flex items-center gap-3 p-3 border-2 border-rose-200 bg-rose-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={selfStudyForm.penalty_enabled}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, penalty_enabled: e.target.checked })}
            className="rounded accent-rose-600"
          />
          <span className="text-sm">
            <strong>자율학습 시간에도 벌점 매기기</strong>
            <span className="block text-gray-600 text-xs mt-0.5">
              기본은 꺼짐 — 지금은 정규 시간에만 벌점이 매겨집니다.
              켜면 자율 시간대(일요일·공휴일은 하루 전체)에 스케줄이 있는데 안 오거나 늦은 경우도
              벌점 검수 목록에 올라옵니다. <strong>교시별이 아니라 하루 1건</strong>으로 판정합니다.
            </span>
          </span>
        </label>

        <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={selfStudyForm.notify}
            onChange={(e) => setSelfStudyForm({ ...selfStudyForm, notify: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm">
            <strong>자율학습 벌점도 알림 보내기</strong>
            <span className="block text-gray-500 text-xs">기본은 꺼짐 — 자율 구간 벌점은 알림 없이 기록만 됩니다.</span>
          </span>
        </label>
      </div>

      <button onClick={handleSaveSelfStudy} className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700">
        저장
      </button>

      <SelfStudyHolidays />
    </div>
  );

  // 이용권(결제) 관리 사용 여부 (2026-08-18)
  const renderMembershipTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-teal-50 rounded-lg">
        <CogIcon className="h-6 w-6 text-teal-600" />
        <div>
          <h3 className="font-medium text-teal-900">이용권(결제) 관리</h3>
          <p className="text-sm text-teal-700">
            이용권을 시스템에 등록해서 관리하시나요? <strong>따로 관리하신다면 꺼 주세요.</strong>
          </p>
        </div>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800 space-y-1">
        <p>⚠️ 켜 두면 <strong>이용권이 없는 회원은 자동으로 '만료'</strong>가 됩니다.</p>
        <p>⚠️ 만료된 회원은 <strong>출결 판정에서 빠집니다</strong> — 안 와도 미출석으로 잡히지 않고, 지각·벌점도 붙지 않습니다.</p>
        <p>ℹ️ 이용권을 장부나 다른 프로그램으로 관리하신다면 <strong>꺼 두셔야</strong> 출결이 정상 동작합니다.</p>
        <p>ℹ️ 끄면 이미 '만료'로 표시된 회원도 <strong>회원 목록을 다시 열 때 정상으로 돌아옵니다.</strong></p>
        <p>ℹ️ 퇴원·휴회 회원은 이 설정과 관계없이 그대로 제외됩니다.</p>
      </div>

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <p className="font-medium">이용권 관리 사용</p>
          <p className="text-sm text-gray-600">
            {membershipForm.voucher_management_enabled
              ? '이용권이 없거나 기간이 지난 회원은 만료로 처리됩니다.'
              : '이용권과 무관하게 모든 재원 회원이 출결 대상으로 유지됩니다.'}
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={membershipForm.voucher_management_enabled}
            onChange={(e) => setMembershipForm({ voucher_management_enabled: e.target.checked })}
            style={{ width: '1.15rem', height: '1.15rem' }}
            className="rounded accent-teal-600"
          />
          <span className="text-sm text-gray-700">
            {membershipForm.voucher_management_enabled ? '사용 중' : '사용 안 함'}
          </span>
        </label>
      </div>

      <button onClick={handleSaveMembership} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
        저장
      </button>
    </div>
  );

  // 스케줄 변경 운영자 승인 필요 여부 (2026-08-19)
  const renderScheduleApprovalTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-sky-50 rounded-lg">
        <CogIcon className="h-6 w-6 text-sky-600" />
        <div>
          <h3 className="font-medium text-sky-900">스케줄 변경 승인</h3>
          <p className="text-sm text-sky-700">
            학부모가 스케줄을 바꿀 때 <strong>운영자가 한 번 더 확인</strong>할지 정합니다.
          </p>
        </div>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800 space-y-1">
        <p>ℹ️ 켜 두면(기본) 학부모가 저장해도 <strong>운영자가 승인해야</strong> 반영됩니다.</p>
        <p>ℹ️ 끄면 학부모가 저장하거나, 학생이 낸 신청에 학부모가 동의한 <strong>그 순간 확정</strong>됩니다.</p>
        <p>⚠️ <strong>끄더라도 적용은 다음 날부터입니다.</strong> 오늘 일정은 바뀌지 않습니다 — 오늘 하루만 늦거나 빠지는 건 사유서로 받으세요.</p>
        <p>ℹ️ 자동으로 확정된 건도 <strong>학생별 스케줄 변경 이력에 남습니다</strong>(회원관리 → 학생 → 스케줄).</p>
      </div>

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <p className="font-medium">운영자 승인 받기</p>
          <p className="text-sm text-gray-600">
            {scheduleApprovalForm.owner_approval_required
              ? '학부모가 저장한 변경은 승인 대기 목록에 쌓입니다.'
              : '학부모가 저장하면 바로 확정됩니다. (적용은 다음 날부터)'}
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={scheduleApprovalForm.owner_approval_required}
            onChange={(e) => setScheduleApprovalForm({ owner_approval_required: e.target.checked })}
            style={{ width: '1.15rem', height: '1.15rem' }}
            className="rounded accent-sky-600"
          />
          <span className="text-sm text-gray-700">
            {scheduleApprovalForm.owner_approval_required ? '승인 받음' : '승인 없이 바로'}
          </span>
        </label>
      </div>

      <button onClick={handleSaveScheduleApproval} className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700">
        저장
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">운영 규칙 관리</h2>
        <button onClick={resetToDefaults} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
          기본값으로 초기화
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('auto-checkout')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'auto-checkout' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}
          >
            자동 퇴실
          </button>
          <button
            onClick={() => setActiveTab('reservation')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'reservation' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600'}`}
          >
            예약
          </button>
          <button
            onClick={() => setActiveTab('movement')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'movement' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-600'}`}
          >
            좌석 이동
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'notifications' ? 'border-yellow-600 text-yellow-600' : 'border-transparent text-gray-600'}`}
          >
            알림
          </button>
          <button
            onClick={() => setActiveTab('self-study')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'self-study' ? 'border-rose-600 text-rose-600' : 'border-transparent text-gray-600'}`}
          >
            자율학습
          </button>
          <button
            onClick={() => setActiveTab('operating-hours')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'operating-hours' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'}`}
          >
            운영시간
          </button>
          <button
            onClick={() => setActiveTab('membership')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'membership' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-600'}`}
          >
            이용권 관리
          </button>
          <button
            onClick={() => setActiveTab('schedule-approval')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'schedule-approval' ? 'border-sky-600 text-sky-600' : 'border-transparent text-gray-600'}`}
          >
            스케줄 승인
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'auto-checkout' && renderAutoCheckoutTab()}
        {activeTab === 'reservation' && renderReservationTab()}
        {activeTab === 'movement' && renderMovementTab()}
        {activeTab === 'notifications' && renderNotificationTab()}
        {activeTab === 'self-study' && renderSelfStudyTab()}
        {activeTab === 'operating-hours' && renderOperatingHoursTab()}
        {activeTab === 'membership' && renderMembershipTab()}
        {activeTab === 'schedule-approval' && renderScheduleApprovalTab()}
      </div>

      {/* Simulation Modal */}
      {showSimulation && simulationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">시뮬레이션 결과</h3>
            <div className="space-y-2">
              <p>
                <strong>시나리오:</strong> {simulationResult.scenario}
              </p>
              <p>
                <strong>예상 조치:</strong> {simulationResult.expected_action}
              </p>
              {simulationResult.warnings_sent && (
                <p>
                  <strong>경고 발송:</strong> {operationalRulesService.formatWarningTimes(simulationResult.warnings_sent)}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowSimulation(false)}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationalRules;
